/**
 * CSV for spreadsheets, German-Excel friendly by default: `;` between
 * fields (Excel in a German locale reads `,` as the decimal mark), CRLF line
 * ends, and a UTF-8 byte order mark on downloads so umlauts survive a
 * double-click open.
 */
import { downloadBlob } from './download';

export interface NxCsvColumn<T> {
  /** Property of the row, dotted for nested values (`customer.name`). */
  key?: string;
  /** Header text; defaults to `key`. */
  label?: string;
  /** Compute the cell from the row instead of reading `key`. */
  value?: (row: T) => unknown;
}

export interface NxCsvOptions<T> {
  /**
   * The columns in order. Plain strings are keys. Left out, object rows use
   * every key they carry (first seen first) and array rows are written as-is
   * without a header.
   */
  columns?: readonly (NxCsvColumn<T> | string)[];
  /** Field separator; defaults to `;`. */
  separator?: string;
  /** Defaults to `\r\n` (RFC 4180). */
  lineEnding?: string;
  /** Write the header row; defaults to `true` when there are columns. */
  header?: boolean;
  /**
   * Prefix text cells that start with `=`, `+`, `-`, `@`, tab or CR with `'`
   * so a spreadsheet shows them instead of running them as a formula
   * („CSV injection“). Numbers are never touched. Defaults to `true`.
   */
  guardFormulas?: boolean;
  /** Turn a raw value into cell text; the default handles dates and nullish. */
  formatValue?: (value: unknown, column: NxCsvColumn<T>) => string;
  /**
   * Start with a UTF-8 byte order mark. Off for `toCsv`, on for
   * `downloadCsv` — Excel needs it for a file, a clipboard does not.
   */
  bom?: boolean;
}

const FORMULA_START = /^[=+\-@\t\r]/;
const PLAIN_NUMBER = /^[+-]?\d+([.,]\d+)?$/;

/**
 * One field, quoted when it holds the separator, a quote, a line break or
 * leading/trailing space; inner quotes are doubled. `null`/`undefined`
 * become an empty field, dates their ISO form.
 */
export function escapeCsvValue(value: unknown, separator = ';'): string {
  if (value === null || value === undefined) return '';
  const text = value instanceof Date ? value.toISOString() : String(value);
  const needsQuotes =
    text.includes(separator) || text.includes('"') || /[\r\n]/.test(text) || text !== text.trim();
  return needsQuotes ? `"${text.replace(/"/g, '""')}"` : text;
}

function readPath(row: unknown, path: string): unknown {
  let current = row;
  for (const part of path.split('.')) {
    if (current === null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

function defaultFormat(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? '' : value.toISOString();
  return String(value);
}

function guard(value: unknown, text: string): string {
  if (typeof value === 'number' || typeof value === 'bigint') return text;
  return FORMULA_START.test(text) && !PLAIN_NUMBER.test(text) ? `'${text}` : text;
}

function inferColumns<T>(rows: readonly T[]): NxCsvColumn<T>[] {
  const keys = new Set<string>();
  for (const row of rows) {
    if (row && typeof row === 'object') for (const key of Object.keys(row)) keys.add(key);
  }
  return [...keys].map((key) => ({ key }));
}

/**
 * Rows as CSV text: objects via `columns` (or their own keys), arrays as
 * they are.
 *
 *     toCsv(invoices, { columns: [{ key: 'number', label: 'Nr.' }, 'total'] })
 */
export function toCsv<T>(rows: readonly T[], options: NxCsvOptions<T> = {}): string {
  const separator = options.separator ?? ';';
  const guardFormulas = options.guardFormulas ?? true;
  const format = options.formatValue ?? defaultFormat;
  const cell = (value: unknown, column: NxCsvColumn<T>) => {
    const text = format(value, column);
    return escapeCsvValue(guardFormulas ? guard(value, text) : text, separator);
  };

  const arrayRows = !options.columns && rows.length > 0 && rows.every(Array.isArray);
  const columns: NxCsvColumn<T>[] = arrayRows
    ? []
    : (options.columns?.map((c) => (typeof c === 'string' ? { key: c } : c)) ?? inferColumns(rows));

  const lines: string[] = [];
  if (arrayRows) {
    for (const row of rows as readonly unknown[][]) {
      lines.push(row.map((value) => cell(value, {})).join(separator));
    }
  } else {
    if (options.header ?? true) {
      lines.push(columns.map((c) => cell(c.label ?? c.key ?? '', c)).join(separator));
    }
    for (const row of rows) {
      lines.push(
        columns
          .map((c) => cell(c.value ? c.value(row) : c.key ? readPath(row, c.key) : '', c))
          .join(separator)
      );
    }
  }
  return (options.bom ? '\uFEFF' : '') + lines.join(options.lineEnding ?? '\r\n');
}

/** Build the CSV and save it; the byte order mark is on unless `bom: false`. */
export function downloadCsv<T>(
  rows: readonly T[],
  fileName: string,
  options: NxCsvOptions<T> = {}
): void {
  const text = toCsv(rows, { ...options, bom: options.bom ?? true });
  downloadBlob(text, fileName, 'text/csv;charset=utf-8');
}
